import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../Button';
import { useToast } from '../../context/ToastContext';
import LoadingSpinner from '../LoadingSpinner';
import AddTeamMember from '../AddTeamMember';
import { getDisplayName } from '../../utils/displayName';

interface TeamMember {
  id: number;
  user: {
    id: number;
    name: string;
    display_name?: string;
    tennisrecord_name?: string | null;
    email: string;
  };
  is_captain: boolean;
}

interface AddMembersStepProps {
  teamId: number;
  onBack: () => void;
  onComplete: () => void;
}

const AddMembersStep: React.FC<AddMembersStepProps> = ({ teamId, onBack, onComplete }) => {
  const toast = useToast();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/teams/${teamId}`);
      // Team members are included in the team object
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      await axios.delete(`/teams/${teamId}/members/${userId}`);
      toast.success('Member removed successfully!');
      fetchMembers();
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Step 5: Add Team Members (Optional)</h2>

      <div className="mb-6">
        <AddTeamMember
          teamId={teamId}
          onMemberAdded={fetchMembers}
          allowCaptainSelection={true}
        />
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Your current roster ({members.length} members)
        </h3>
        {members.length > 0 ? (
          <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {members.map((member) => (
                  <tr key={member.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {getDisplayName(member.user)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {member.user.email}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {member.is_captain ? (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">
                          Captain
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded">
                          Player
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {!member.is_captain && (
                        <button
                          onClick={() => handleRemoveMember(member.user.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No members added yet.</p>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onComplete}>
            Skip for now
          </Button>
          <Button variant="primary" onClick={onComplete}>
            Complete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddMembersStep;

